import { useCreateNote } from "@/api/notes/notes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface NewNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (noteId: string) => void;
}

type FormValues = { title: string };

export function NewNoteModal({
  open,
  onOpenChange,
  onCreated,
}: NewNoteModalProps) {
  const { t } = useTranslation();
  const createNote = useCreateNote();

  const form = useForm<FormValues>({
    defaultValues: { title: "" },
  });

  const handleSubmit = form.handleSubmit((values) => {
    createNote.mutate(
      { title: values.title.trim() || t("notes.newNoteTitle") },
      {
        onSuccess: (note) => {
          form.reset({ title: "" });
          onOpenChange(false);
          onCreated?.(note.id);
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("notes.newNoteModalTitle")}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              {...form.register("title", { required: true })}
              placeholder={t("notes.newNoteTitle")}
              id="note-title"
              className="w-full"
            />
            {form.formState.errors.title && (
              <p className="text-sm text-destructive mt-1">
                {t("notes.newNoteTitle")}
              </p>
            )}
          </div>
          <DialogFooter className="sm:justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("notes.actionCancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createNote.isPending}>
              {t("notes.add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
